<template>
  <div>
    <label v-if="label" class="tw-mb-1 tw-block tw-text-xs dark:tw-text-white">
      {{ label }}
    </label>
    <div
      :class="{
        'tw-w-full tw-rounded-md tw-border tw-bg-white hover:tw-border-deep-orange-500 dark:tw-bg-black dark:tw-text-white': true,
        'focus-within:tw-border-transparent focus-within:tw-ring-2 focus-within:tw-ring-deep-orange-500 hover:focus-within:tw-border-transparent':
          !disabled && !error,
        'tw-cursor-not-allowed tw-border-gray-300 tw-bg-gray-100 tw-text-gray-500 dark:tw-border-gray-700 dark:tw-bg-gray-800 dark:tw-text-gray-400':
          disabled,
        'tw-border-red-300 dark:tw-border-red-700': error
      }"
    >
      <input
        :id="id || labelBasedId"
        ref="input"
        type="time"
        :name="id || labelBasedId"
        :data-testid="id || labelBasedId"
        :class="{
          'time-picker': true,
          'tw-w-full tw-rounded-md tw-bg-white tw-p-2 tw-text-sm tw-outline-none focus:tw-outline-none focus:tw-ring-0 dark:tw-bg-black': true
        }"
        :value="timeSelected"
        :min="min"
        :max="max"
        :required="required"
        :disabled="disabled"
        :title="getTitle"
        :list="`time_list_${id || labelBasedId}`"
        @input="onInput"
        @dblclick="forceShowPicker"
      />
      <datalist :id="`time_list_${id || labelBasedId}`">
        <option v-for="time in timeList" :key="time" :value="time" />
      </datalist>
    </div>
    <div v-if="error" class="tw-mt-1 tw-text-2xs tw-text-red-700">
      {{ error }}
    </div>
  </div>
</template>

<script lang="ts">
import logger from '@goldcast/logger';
import { defineComponent, nextTick } from 'vue';

import { padZero } from '../utils/date';

const TimeList: string[] = [];
let hour = 0;
while (hour < 24) {
  let minute = 0;
  while (minute < 60) {
    TimeList.push(`${padZero(hour)}:${padZero(minute)}`);
    minute += 30;
  }
  hour++;
}

export default defineComponent({
  name: 'StudioTimeInput',
  props: {
    id: {
      type: String,
      default: ''
    },
    modelValue: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    min: {
      type: String,
      default: '00:00'
    },
    max: {
      type: String,
      default: '23:59'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    disabledReason: {
      type: String,
      default: 'Time picker is disabled'
    },
    required: {
      type: Boolean,
      default: false
    },
    skipValidationBasedOnInitialValue: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      timeSelected: this.modelValue,
      initialTimeSelected: this.modelValue,
      timeList: TimeList
    };
  },
  mounted() {
    nextTick(() => {
      this.timeSelected = this.modelValue;
      this.initialTimeSelected = this.modelValue;
    });
  },
  computed: {
    error(): boolean | string {
      if (this.skipValidationBasedOnInitialValue && this.timeSelected === this.initialTimeSelected) {
        return false;
      }

      const hoursSelected = parseInt(this.timeSelected.split(':')[0]);
      const minutesSelected = parseInt(this.timeSelected.split(':')[1]);

      const hoursMin = parseInt(this.min.split(':')[0]);
      const minutesMin = parseInt(this.min.split(':')[1]);

      const hoursMax = parseInt(this.max.split(':')[0]);
      const minutesMax = parseInt(this.max.split(':')[1]);

      if (hoursSelected < hoursMin) {
        return `Value must be after ${this.convertTo12HourFormat(this.min)}`;
      }

      if (hoursSelected > hoursMax) {
        return `Value must be before ${this.convertTo12HourFormat(this.max)}`;
      }

      if (hoursSelected === hoursMin && minutesSelected < minutesMin) {
        return `Value must be after ${this.convertTo12HourFormat(this.min)}`;
      }

      if (hoursSelected === hoursMax && minutesSelected > minutesMax) {
        return `Value must be before ${this.convertTo12HourFormat(this.max)}`;
      }

      return false;
    },
    labelBasedId(): string {
      return this.label.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
    },
    getTitle(): string {
      if (this.disabled) {
        return this.disabledReason;
      }
      if (this.error) {
        return 'Invalid time. Double click to open time picker or type the time in 24-hour format.';
      }
      return 'Double click to open time picker or type the time in 24-hour format.';
    }
  },
  methods: {
    onInput(e) {
      if (!e.target.value) {
        return;
      }

      this.timeSelected = e.target.value;
      this.$emit('update:modelValue', e.target.value);
    },
    /** @public */
    hasError(): boolean {
      return !!this.error;
    },
    /** @public */
    forceChangeTimeSelected() {
      this.$nextTick(() => {
        this.timeSelected = this.modelValue;
      });
    },
    convertTo12HourFormat(time: string): string {
      const hours = parseInt(time.split(':')[0]);
      const minutes = parseInt(time.split(':')[1]);

      const hours12 = hours % 12 || 12;
      const amPm = hours < 12 || hours === 24 ? 'AM' : 'PM';

      return `${hours12}:${padZero(minutes)} ${amPm}`;
    },
    forceShowPicker() {
      if (!('showPicker' in HTMLInputElement.prototype)) {
        return;
      }

      if (!this.$refs.input) {
        return;
      }

      try {
        (this.$refs.input as any).showPicker();
      } catch (error) {
        logger.error('Error while trying to show time picker', error);
      }
    }
  }
});
</script>

<style lang="scss">
.time-picker::-webkit-calendar-picker-indicator {
  cursor: pointer;
}
</style>
